import { apply, burst, celebrate, give, onceForever, onceToday, toast } from '../core/effects';
import { S } from '../core/game';
import { dayId } from '../core/state';
import { m } from '../i18n';
import { npcName, objName } from '../i18n/labels';
import { sfx } from '../audio/sfx';
import { sayEnglish } from '../audio/speech';
import { say, type Choice } from '../ui/dialog';
import { openMinigame } from '../minigames/registry';
import type { ObjectId } from '../world/mapdef';
import { stamp } from './passport';
import { mrtMenu, order, quiz, type FoodSpec } from './trips';
import { registerObjects, registerTalk } from './registry';

/** Interactions of Ximending and Longshan Temple (ximen package). */
const t = () => m().ximen;
const pick = <T>(a: readonly T[]): T => a[Math.floor(Math.random() * a.length)];
const shuffle = <T>(a: T[]): T[] => a.map((v) => [Math.random(), v] as const).sort((x, y) => x[0] - y[0]).map((p) => p[1]);
const back = (): Choice => ({ text: m().common.back });
const evening = () => S().time >= 17 * 60 || S().time < 60;

/** A first-time learning reward (stars once ever, a little study once a day). */
function learned(key: string, stars = 2): void {
  if (onceForever(`xm.${key}`)) {
    apply({ stars, study: 4 });
    toast(t().common.learned);
  } else if (onceToday(`xm.${key}`)) apply({ study: 2 });
}

/** A short fact with an English phrase to listen to and repeat. */
function fact(id: ObjectId, icon: string, text: string, en: string, key: string, then?: () => void): void {
  sayEnglish(en, 0.85);
  say({ who: objName(id), icon, text: `${text}\n\n🗣️ “${en}”`, en, choices: [{ text: '👍', run: () => (learned(key, 1), then?.()) }] });
}

/** A landmark quiz (answers[0] is right), with a reward the first time. */
function landmarkQuiz(id: ObjectId, icon: string, q: { text: string; q: string; a: readonly string[]; right: string; wrong: string }, key: string, extra?: () => void): void {
  quiz(objName(id), icon, q.text, q.q, q.a, q.right, q.wrong, () => {
    learned(key);
    extra?.();
  });
}

function food(id: ObjectId, icon: string, f: Omit<FoodSpec, 'price'>, price: number, fx: { energy?: number; mood?: number }, after?: () => void): void {
  order(id, icon, { ...f, price }, fx, after);
}

/** Several questions in a row (each answers[0] is right); `done` runs after the last one. */
function chain(who: string, icon: string, intro: string, qs: readonly { q: string; a: readonly string[] }[], right: string, wrong: string, done: () => void, i = 0): void {
  if (i >= qs.length) return done();
  const { q, a } = qs[i];
  sayEnglish(q, 0.85);
  say({
    who,
    icon,
    text: `${i === 0 ? `${intro}\n\n` : ''}${q}`,
    choices: shuffle(a.map((txt, k) => ({ txt, ok: k === 0 }))).map((o) => ({
      text: o.txt,
      run: () => {
        sayEnglish(o.txt, 0.9);
        if (o.ok) {
          sfx('ok');
          say({ who, icon, text: right, choices: [{ text: '➡️', run: () => chain(who, icon, intro, qs, right, wrong, done, i + 1) }] });
        } else {
          sfx('bad');
          say({ who, icon, text: wrong, choices: [{ text: m().common.back, run: () => chain(who, icon, intro, qs, right, wrong, done, i) }] });
        }
      },
    })),
  });
}

function pay(n: number): boolean {
  if (S().coins < n) {
    toast(t().common.notEnough);
    return false;
  }
  apply({ coins: -n });
  return true;
}

// --- Ximending -----------------------------------------------------------------------------------

function exit6(): void {
  const e = t().exit6;
  sayEnglish(e.phrase, 0.85);
  say({
    who: objName('xm_exit6'),
    icon: '🚇',
    text: `${e.text}\n\n🗣️ “${e.phrase}”`,
    en: e.phrase,
    choices: [
      { text: e.ride, run: () => mrtMenu() },
      { text: e.practice, run: () => quiz(objName('xm_exit6'), '🚇', e.text, e.q, e.a, e.right, e.wrong, () => learned('exit6')) },
      back(),
    ],
  });
}

function cinema(): void {
  const c = t().cinema;
  say({
    who: objName('xm_cinema'),
    icon: '🎬',
    text: c.text,
    choices: [
      ...c.films.map((film): Choice => ({
        text: `🎞️ ${film}`,
        cost: m().world3.market.cost(50),
        run: () => {
          const f = (s: string) => s.replace('{film}', film);
          food('xm_cinema', '🎬', { vendor: c.vendor, ok: f(c.ok), bad: c.bad.map(f), done: f(c.done) }, 50, { mood: 18 }, () => {
            apply({ minutes: 100 }, { float: false });
            window.setTimeout(() => toast(f(c.watched)), 400);
          });
        },
      })),
      back(),
    ],
  });
}

function claw(): void {
  const c = t().claw;
  say({
    who: objName('xm_claw'),
    icon: '🧸',
    text: c.text,
    choices: [{ text: c.play, run: () => pay(10) && openMinigame('claw') }, back()],
  });
}

function tattoo(): void {
  const c = t().tattoo;
  say({
    who: objName('xm_tattoo'),
    icon: '🖋️',
    text: c.text,
    choices: [
      {
        text: c.get,
        run: () => {
          if (!pay(10)) return;
          sfx('pop');
          burst('sparkle', 8);
          apply({ mood: 8 });
          sayEnglish(c.done, 0.9);
          say({ who: objName('xm_tattoo'), icon: '⭐', text: c.done, en: c.done });
        },
      },
      back(),
    ],
  });
}

function sticker(): void {
  const c = t().sticker;
  say({
    who: objName('xm_sticker'),
    icon: '📸',
    text: c.text,
    choices: [
      ...c.frames.map((fr): Choice => ({
        text: fr,
        cost: m().world3.market.cost(30),
        run: () => {
          if (!pay(30)) return;
          sayEnglish(c.cheese, 1);
          sfx('ring');
          burst('heart', 8);
          apply({ mood: 12 });
          if (onceToday('xm.sticker')) apply({ stars: 1 }, { float: false });
          say({ who: objName('xm_sticker'), icon: '📸', text: `🗣️ “${c.cheese}”\n\n${c.done} ${fr}`, en: c.cheese });
        },
      })),
      back(),
    ],
  });
}

function busker(): void {
  const b = t().busker;
  const tm = S().time;
  const who = objName('xm_busker');
  const kind = tm >= 10 * 60 && tm < 17 * 60 + 30 ? 'dancer' : tm >= 17 * 60 + 30 && tm < 22 * 60 ? 'singer' : null;
  if (!kind) return say({ who, icon: '🎭', text: b.none });
  say({
    who,
    icon: kind === 'dancer' ? '🕺' : '🎸',
    text: `${b[kind]}\n\n🗣️ “${b.cheer}”`,
    en: b.cheer,
    choices: [
      {
        text: b.watch,
        cost: m().common.minutes(15),
        run: () => {
          sfx('fanfare');
          burst('note', 10);
          apply({ minutes: 15, ...(onceToday('xm.busker') ? { mood: 14 } : {}) });
          stamp('ximen');
          sayEnglish(b.cheer, 0.9);
          toast(b.watched);
        },
      },
      {
        text: b.tip,
        run: () => {
          if (!pay(10)) return;
          sfx('drop');
          apply({ mood: 5 });
          sayEnglish(b.thanks, 0.9);
          say({ who, icon: '🙇', text: b.thanks, en: b.thanks });
        },
      },
      back(),
    ],
  });
}

function market(): void {
  const k = t().market;
  const who = objName('xm_market');
  if (!['sat', 'sun'].includes(dayId(S()))) return say({ who, icon: '🎪', text: k.text });
  quiz(who, '🎪', k.text, k.q, k.a, k.right, k.wrong, () => {
    learned('market', 1);
    window.setTimeout(
      () =>
        say({
          who,
          icon: '🔑',
          text: k.right,
          choices: [{ text: k.buy, run: () => pay(30) && (give('keychain'), sfx('ok'), toast(k.bought)) }, back()],
        }),
      50,
    );
  });
}

// --- Longshan Temple -----------------------------------------------------------------------------

function templeStamp(): void {
  if (stamp('longshan')) celebrate();
}

function etiquette(): void {
  const e = t().etiquette;
  chain(npcName('xm_volunteer'), '🙏', e.intro, e.qs, e.right, e.wrong, () => {
    learned('etiquette', 3);
    templeStamp();
    sayEnglish(e.done, 0.85);
    say({ who: npcName('xm_volunteer'), icon: '🐉', text: e.done, en: e.done });
  });
}

function burner(): void {
  const b = t().burner;
  say({
    who: objName('xm_burner'),
    icon: '🪔',
    text: `${b.text}\n\n🗣️ “${b.wish}”`,
    en: b.wish,
    choices: [
      {
        text: b.bow,
        run: () => {
          sfx('bell');
          burst('sparkle', 10);
          if (onceToday('xm.burner')) apply({ mood: 10 });
          sayEnglish(b.wish, 0.85);
          templeStamp();
          toast(b.done);
        },
      },
      back(),
    ],
  });
}

function moonBlocks(): void {
  const mb = t().moon;
  const who = objName('xm_moon');
  const throwIt = (): void => {
    sfx('drop');
    const r = Math.random();
    const res = mb.results[r < 0.5 ? 0 : r < 0.75 ? 1 : 2];
    sayEnglish(res.en, 0.85);
    if (onceToday('xm.moon')) apply({ mood: 6, study: 2 });
    templeStamp();
    say({ who, icon: '🌙', text: `${res.name}\n\n${res.text}\n\n🗣️ “${res.en}”`, en: res.en, choices: [{ text: mb.again, run: throwIt }, back()] });
  };
  say({ who, icon: '🌙', text: mb.text, choices: [{ text: mb.throw, run: throwIt }, back()] });
}

function fortuneSticks(): void {
  const st = t().sticks;
  const who = objName('xm_sticks');
  say({
    who,
    icon: '🎋',
    text: st.text,
    choices: [
      {
        text: st.shake,
        run: () => {
          sfx('rustle');
          const n = 1 + Math.floor(Math.random() * 100);
          const f = st.fortunes[n % st.fortunes.length];
          sayEnglish(f, 0.85);
          if (onceToday('xm.sticks')) apply({ mood: 6 });
          templeStamp();
          say({ who, icon: '📜', text: `${st.got.replace('{n}', String(n))}\n\n🗣️ “${f}”`, en: f });
        },
      },
      back(),
    ],
  });
}

function fortuneTeller(): void {
  const f = t().fortune;
  const read = pick(f.reads);
  sayEnglish(read, 0.85);
  say({
    who: npcName('xm_fortuneGrandpa'),
    icon: '🔮',
    text: `${f.text}\n\n🗣️ “${read}”`,
    en: read,
    choices: [{ text: `🗣️ ${f.thanks}`, run: () => (sayEnglish(f.thanks, 0.9), onceToday('xm.fortune') && apply({ mood: 6 })) }],
  });
}

function hxFood(id: 'xm_hxNoodle' | 'xm_hxSoup'): void {
  if (!evening()) return say({ who: objName(id), icon: '🏮', text: t().common.closed });
  const f = id === 'xm_hxNoodle' ? t().hxNoodle : t().hxSoup;
  food(id, id === 'xm_hxNoodle' ? '🍜' : '🍲', f, id === 'xm_hxNoodle' ? 50 : 45, { energy: 15, mood: 5 });
}

function chess(): void {
  landmarkQuiz('xm_chess', '♟️', t().chess, 'chess');
}

function tourist(which: 'tourist' | 'tourist2'): void {
  const q = t()[which];
  const id = which === 'tourist' ? 'xm_tourist' : 'xm_tourist2';
  quiz(npcName(id), '🧳', '', q.ask, q.a, q.right, q.wrong, () => learned(which, 2));
}

const OBJ: Partial<Record<ObjectId, () => void>> = {
  xm_exit6: exit6,
  xm_mapBoard: () => landmarkQuiz('xm_mapBoard', '🗺️', t().mapBoard, 'mapBoard'),
  xm_meesua: () => food('xm_meesua', '🍜', t().meesua, 45, { energy: 14, mood: 6 }, () => {
    stamp('ximen');
    window.setTimeout(() => toast(t().meesua.after), 1500);
  }),
  xm_tea: () => food('xm_tea', '🧋', t().tea, 40, { energy: 6, mood: 10 }),
  xm_chicken: () => food('xm_chicken', '🍗', t().chicken, 60, { energy: 18, mood: 6 }),
  xm_ice: () => food('xm_ice', '🍧', t().ice, 70, { energy: 8, mood: 14 }),
  xm_claw: claw,
  xm_cinema: cinema,
  xm_posters: () => landmarkQuiz('xm_posters', '🎞️', t().posters, 'posters'),
  xm_sneakers: () => landmarkQuiz('xm_sneakers', '👟', t().sneakers, 'sneakers'),
  xm_clothes: () => landmarkQuiz('xm_clothes', '👕', t().clothes, 'clothes'),
  xm_tattoo: tattoo,
  xm_sticker: sticker,
  xm_busker: busker,
  xm_screen: () => landmarkQuiz('xm_screen', '📺', t().screen, 'screen'),
  xm_plaque: () => fact('xm_plaque', '🔖', t().plaque.text, t().plaque.phrase, 'plaque'),
  xm_graffiti: () => landmarkQuiz('xm_graffiti', '🎨', t().graffiti, 'graffiti'),
  xm_redHouse: () => landmarkQuiz('xm_redHouse', '🧱', t().redHouse, 'redHouse', () => stamp('ximen')),
  xm_market: market,

  xm_mrtL: () => say({ who: objName('xm_mrtL'), icon: '🚇', text: t().mrtL.text, choices: [{ text: t().mrtL.ride, run: () => mrtMenu() }, back()] }),
  xm_dragonDoor: etiquette,
  xm_pillar: () => landmarkQuiz('xm_pillar', '🐲', t().pillar, 'pillar', templeStamp),
  xm_waterfall: () => fact('xm_waterfall', '⛲', t().waterfall.text, t().waterfall.phrase, 'waterfall', () => onceToday('xm.pond') && apply({ mood: 4 })),
  xm_burner: burner,
  xm_offer: () => landmarkQuiz('xm_offer', '🍍', t().offer, 'offer'),
  xm_moon: moonBlocks,
  xm_sticks: fortuneSticks,
  xm_roof: () => landmarkQuiz('xm_roof', '🏯', t().roof, 'roof', templeStamp),
  xm_herbTea: () => food('xm_herbTea', '🌿', t().herbTea, 20, { energy: 6, mood: 4 }),
  xm_herbSign: () => fact('xm_herbSign', '🌿', t().herbSign.text, t().herbSign.phrase, 'herbAlley'),
  xm_bopiliao: () => landmarkQuiz('xm_bopiliao', '🧱', t().bopiliao, 'bopiliao'),
  xm_huaxi: () => say({ who: objName('xm_huaxi'), icon: '🏮', text: t().huaxi.text }),
  xm_hxNoodle: () => hxFood('xm_hxNoodle'),
  xm_hxSoup: () => hxFood('xm_hxSoup'),
  xm_chess: chess,
  xm_pigeons: () => landmarkQuiz('xm_pigeons', '🐦', t().pigeons, 'pigeons'),
  xm_canoe: () => fact('xm_canoe', '🛶', t().canoe.text, t().canoe.phrase, 'canoe'),
  xm_fortune: fortuneTeller,
  xm_oldIce: () => food('xm_oldIce', '🍧', t().oldIce, 40, { energy: 8, mood: 12 }),
};
registerObjects(OBJ);

const ICONS: Record<string, string> = {
  xm_teen1: '📱', xm_teen2: '📱', xm_teen3: '🧢', xm_shopper1: '🛍️', xm_shopper2: '👟', xm_skater: '🛹', xm_artist: '🎨',
  xm_redGuide: '🧱', xm_cosplayer: '✨', xm_grandpaX: '🎞️', xm_worshipper1: '🙏', xm_worshipper2: '🙏', xm_incenseAuntie: '🌸',
  xm_chess2: '♟️', xm_pigeonKid: '🐦', xm_bopiliaoGuide: '📚', xm_nightVendor: '🏮', xm_taichi: '🧘',
};

registerTalk((id) => {
  if (!id.startsWith('xm_')) return false;
  switch (id) {
    case 'xm_meesuaLady':
      OBJ.xm_meesua!();
      return true;
    case 'xm_tourist':
      tourist('tourist');
      return true;
    case 'xm_tourist2':
      tourist('tourist2');
      return true;
    case 'xm_volunteer':
      etiquette();
      return true;
    case 'xm_fortuneGrandpa':
      fortuneTeller();
      return true;
    case 'xm_herbLady':
      OBJ.xm_herbTea!();
      return true;
    case 'xm_chess1':
      chess();
      return true;
    case 'xm_artist':
      if (['sat', 'sun'].includes(dayId(S()))) {
        market();
        return true;
      }
      break;
    case 'xm_nightVendor':
      if (evening()) {
        OBJ.xm_hxNoodle!();
        return true;
      }
      break;
    case 'xm_incenseAuntie': {
      const lines = t().talk[id];
      say({
        who: npcName(id),
        icon: '🌸',
        text: pick(lines),
        choices: [{ text: t().incenseBuy, run: () => pay(5) && (sfx('ok'), toast(t().incenseBought)) }, back()],
      });
      return true;
    }
    default:
      break;
  }
  const lines = t().talk[id];
  if (!lines?.length) return false;
  const line = pick(lines);
  const en = line.match(/“([^”]+)”/)?.[1];
  if (en) sayEnglish(en, 0.9);
  say({ who: npcName(id), icon: ICONS[id] ?? '💬', text: line, en });
  return true;
});
